import Cookies from 'universal-cookie';
import { getWindowName, WindowNameEnum } from '@jooxter/utils/src/window';
import { getDomain } from './utils';
import { CookieOptions } from './types';

type CookieDefaultOptions = Omit<CookieOptions, 'expires'>;

const baseOptions: CookieDefaultOptions = {
  path: '/',
  domain: getDomain(),
  secure: true,
  sameSite: 'strict',
  partitioned: false,
  httpOnly: false,
};

// Embedded as a 3rd-party iframe inside Teams, cookies only survive Chrome's third-party cookie
// restrictions with SameSite=None + Partitioned (CHIPS). Detected via window.name (see
// @jooxter/utils' getWindowName / setWindowName) so it also covers the webapp once the Teams
// "App" tab has redirected its own frame to it. 'strict' / not partitioned everywhere else —
// that's what production ships for the normal top-level webapp.
const teamsOptions: CookieDefaultOptions = {
  ...baseOptions,
  sameSite: 'none',
  partitioned: true,
};

/**
 * The methods in this service should be called only by the store
 */
export const CookieService = {
  cookies: new Cookies(),

  get setOptions(): CookieDefaultOptions {
    return getWindowName() === WindowNameEnum.TEAMS ? teamsOptions : baseOptions;
  },

  setCookie(name: string, value: string, overrideOptions?: CookieOptions): void {
    const twoYears = new Date();
    twoYears.setFullYear(new Date().getFullYear() + 2);
    const expirationDate = overrideOptions?.expires ? new Date(overrideOptions.expires) : twoYears;
    let options = { ...this.setOptions, expires: expirationDate };

    if (overrideOptions) {
      options = { ...options, ...overrideOptions, expires: expirationDate };
    }

    this.cookies.set(name, value, {
      ...options,
    });
  },

  getCookie(name: string): string {
    return this.cookies.get(name);
  },

  deleteCookie(name: string, overrideOptions?: CookieOptions): void {
    let options = { ...this.setOptions };

    if (overrideOptions) {
      options = { ...options, ...overrideOptions };
    }
    return this.cookies.remove(name, options);
  },

  async deleteAll(): Promise<void> {
    const cookieNames = (await cookieStore.getAll())
      .map((cookie) => cookie.name)
      .filter((name): name is string => !!name);
    await Promise.all(cookieNames.map((name) => cookieStore.delete(name)));
  },
};
